import React, { useCallback, useState } from 'react';
import { components } from 'react-select';
import classNames from 'classnames';
import cogoToast from 'cogo-toast';
import PropTypes from 'prop-types';

import { DEBOUNCE_DURATION } from 'data/asyncSelect';
import { useLazySearchSuperBatchesGroupQuery } from 'services/superBatchService';
import FieldErrors from 'components/FieldErrors';
import GlobalLoader from 'components/GlobalLoader';
import Select from 'components/Select';
import styles from './SuperBatchGroupSelect.module.scss';
import SuperBatchTypeChip from 'components/SuperBatchTypeChip';
import useDebouncedPromise from 'hooks/useDebouncedPromise';

const MIN_KEYWORD_LENGTH = 3;

function SuperBatchOption(props) {
  return (
    <components.Option {...props}>
      <div className="h4 row">
        {props.data.id}
        {props.data.group_name && (
          <SuperBatchTypeChip batchType={props.data.group_name} />
        )}
      </div>
    </components.Option>
  );
}

export default function SuperBatchGroupSelect({
  excludedSuperBatchIds = [],
  batchTypes = [],
  isDisabled,
  value,
  onChange,
  containerClassName,
  selectedSuperBatch,
  ...remainingProps
}) {
  const [superBatches, setSuperBatches] = useState(value);
  const [fetchSuperBatchesGroup, { isFetching }] =
    useLazySearchSuperBatchesGroupQuery();

  const handleOnChange = useCallback(
    (option) => {
      setSuperBatches(option);
      onChange && onChange(option);
    },
    [onChange]
  );

  const generateOptions = useCallback(
    (superbatches) =>
      superbatches.map((o) => ({
        value: o.id,
        label: o.group_name,
        ...o,
      })),
    []
  );

  let response;

  const loadOptions = useDebouncedPromise(
    async (keyword, callback) => {
      if (keyword.length < MIN_KEYWORD_LENGTH) return callback([]);
      response = await fetchSuperBatchesGroup({
        keyword,
      });
      let options = [];
      if (response.data) {
        options = generateOptions(response.data.data);
      } else {
        cogoToast.error(
          response.error?.data?.message ||
            'Failed to load super batches! Try again after sometime'
        );
      }
      callback(options);
    },
    DEBOUNCE_DURATION,
    [excludedSuperBatchIds, generateOptions]
  );

  const noOptionsMessage = useCallback(
    ({ inputValue }) => {
      if (inputValue.length < MIN_KEYWORD_LENGTH) {
        return `Type atleast ${MIN_KEYWORD_LENGTH} characters to search`;
      } else if (isFetching) {
        return `Searching...`;
      } else {
        return `No matching super batches found.`;
      }
    },
    [isFetching]
  );

  return (
    <div className={classNames(styles.container, containerClassName)}>
      <GlobalLoader isVisible={isFetching} />
      <Select
        type="async"
        placeholder="Select Batch"
        components={{
          Option: SuperBatchOption,
        }}
        loadOptions={loadOptions}
        noOptionsMessage={noOptionsMessage}
        value={superBatches}
        onChange={handleOnChange}
        {...remainingProps}
      />
      <FieldErrors
        errors={response?.error?.data?.message}
        className="m-t-xs"
      />
    </div>
  );
}

SuperBatchGroupSelect.propTypes = {
  excludedSuperBatchIds: PropTypes.arrayOf(PropTypes.number),
};
